import type { ActivityDefinition, PlanDefinition } from "@medplum/fhirtypes";
import { toArray } from "@tanstack/db";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { useCollection } from "../core/hooks";
import { revisionSnapshot } from "./definition-revision-snapshot";
import { type DefinitionRevisionChange, diffDefinitionRevisions } from "./definition-version-diff";
import type { PublicationStatus } from "./shared/types";

/**
 * Two kinds are versioned but cannot go through the shared query below.
 *
 * A device definition keeps its lineage and revision in `identifier` — R4 spends
 * `url` and `version` on something else — and reads through
 * `useDeviceDefinitionVersionHistory`. A finding is a Questionnaire sharing one
 * collection with every other Questionnaire, so its lineage is found through
 * `catalogModelKind` / `catalogVersion` and reads through
 * `useFindingDefinitionVersionHistory`. Both reach this type only as the `kind`
 * on the DTO; the diffing is the shared one either way.
 */
export type VersionedDefinitionKind = "diagnostic" | "treatment" | "protocol" | "program" | "device" | "finding";

/** The kinds whose lineage IS `url` and whose revision IS `version`. */
export type SharedVersionedDefinitionKind = Exclude<VersionedDefinitionKind, "device" | "finding">;

export interface DefinitionVersionHistoryEntry {
  resourceId: string;
  version: string;
  authoredAt?: string;
  /** Display name of whoever wrote the revision. Absent when unattributed — a
      bot-written or seeded revision carries no `meta.author.display`. */
  author?: string;
  status: PublicationStatus;
  current: boolean;
  changes: DefinitionRevisionChange[];
}

export interface DefinitionVersionHistoryDto {
  kind: VersionedDefinitionKind;
  definitionUrl: string;
  versions: DefinitionVersionHistoryEntry[];
}

export interface DefinitionVersionHistoryFilters {
  definitionUrl?: string;
}

type VersionedDefinitionResource = ActivityDefinition | PlanDefinition;
type VersionedCollectionName = "activityDefinition" | "planDefinition";
interface VersionHistoryQueryRow {
  definitionUrl: string;
  revisions: VersionedDefinitionResource[];
}

const COLLECTION_BY_KIND: Record<SharedVersionedDefinitionKind, VersionedCollectionName> = {
  diagnostic: "activityDefinition",
  treatment: "activityDefinition",
  protocol: "planDefinition",
  program: "planDefinition",
};

export function useDefinitionVersionHistory(
  kind: SharedVersionedDefinitionKind,
  definitionUrl: string | undefined,
): DefinitionVersionHistoryDto | undefined {
  const collection = useCollection<VersionedDefinitionResource>(COLLECTION_BY_KIND[kind]);
  const result = useLiveQuery({
    query: (query) => {
      if (!definitionUrl) return undefined;
      const lineage = query
        .from({ definition: collection })
        .where(({ definition }) => eq(definition.url, definitionUrl))
        .groupBy(({ definition }) => definition.url)
        .select(({ definition }) => ({ definitionUrl: definition.url }));
      return query
        .from({ lineage })
        .select(({ lineage }) => ({
          definitionUrl: lineage.definitionUrl,
          revisions: toArray(
            query
              .from({ revision: collection })
              .where(({ revision }) => eq(revision.url, lineage.definitionUrl))
              .orderBy(({ revision }) => revision.version, {
                direction: "desc",
                stringSort: "locale",
                locale: "en-US",
                localeOptions: { numeric: true },
              })
              .orderBy(({ revision }) => revision.id, "asc")
              .select(({ revision }) => ({ ...revision })),
          ),
        }))
        .findOne();
    },
  });

  // TanStack DB has no adjacent-row/LAG expression for decoded recursive
  // snapshots; the query owns lineage composition and this pure step only diffs.
  return result.data ? projectVersionHistoryRow(kind, result.data as VersionHistoryQueryRow) : undefined;
}

export function useDefinitionVersionHistories(
  kind: SharedVersionedDefinitionKind,
  filters: DefinitionVersionHistoryFilters = {},
): DefinitionVersionHistoryDto[] {
  const collection = useCollection<VersionedDefinitionResource>(COLLECTION_BY_KIND[kind]);
  const result = useLiveQuery({
    query: (query) => {
      const definitions = query.from({ definition: collection });
      const selected = filters.definitionUrl
        ? definitions.where(({ definition }) => eq(definition.url, filters.definitionUrl))
        : definitions;
      const lineages = selected
        .groupBy(({ definition }) => definition.url)
        .select(({ definition }) => ({ definitionUrl: definition.url }));
      return query.from({ lineage: lineages }).select(({ lineage }) => ({
        definitionUrl: lineage.definitionUrl,
        revisions: toArray(
          query
            .from({ revision: collection })
            .where(({ revision }) => eq(revision.url, lineage.definitionUrl))
            .orderBy(({ revision }) => revision.version, {
              direction: "desc",
              stringSort: "locale",
              locale: "en-US",
              localeOptions: { numeric: true },
            })
            .orderBy(({ revision }) => revision.id, "asc")
            .select(({ revision }) => ({ ...revision })),
        ),
      }));
    },
  });

  // Same adjacent decoded-snapshot limitation as the singular hook.
  return ((result.data as VersionHistoryQueryRow[] | undefined) ?? []).map((row) =>
    projectVersionHistoryRow(kind, row),
  );
}

function projectVersionHistoryRow(
  kind: SharedVersionedDefinitionKind,
  { definitionUrl, revisions }: VersionHistoryQueryRow,
): DefinitionVersionHistoryDto {
  return buildVersionHistory(
    kind,
    definitionUrl,
    revisions.map((definition) => ({
      resourceId: definition.id ?? "",
      version: definition.version ?? "1",
      ...(definition.date || definition.meta?.lastUpdated
        ? { authoredAt: definition.date || definition.meta?.lastUpdated }
        : {}),
      ...(definition.meta?.author?.display ? { author: definition.meta.author.display } : {}),
      status: (definition.status as PublicationStatus | undefined) ?? "unknown",
      snapshot: revisionSnapshot(kind, definition),
    })),
  );
}

/** One revision, already reduced to what the history renders and diffs. */
export interface VersionHistoryRevision {
  resourceId: string;
  version: string;
  authoredAt?: string;
  author?: string;
  status: PublicationStatus;
  /** The substantive content of this revision; two adjacent ones are diffed. */
  snapshot: unknown;
}

/**
 * Turn newest-first revisions into the history DTO. Shared with the device
 * kind, whose revisions are found by a different query but diffed identically —
 * "what changed between these two" is not per-resource-type behaviour.
 */
export function buildVersionHistory(
  kind: VersionedDefinitionKind,
  definitionUrl: string,
  revisions: readonly VersionHistoryRevision[],
): DefinitionVersionHistoryDto {
  return {
    kind,
    definitionUrl,
    versions: revisions.map((revision, index) => ({
      resourceId: revision.resourceId,
      version: revision.version,
      ...(revision.authoredAt === undefined ? {} : { authoredAt: revision.authoredAt }),
      ...(revision.author === undefined ? {} : { author: revision.author }),
      status: revision.status,
      current: index === 0,
      changes:
        index === revisions.length - 1
          ? [{ label: "Definition created", type: "created" }]
          : diffDefinitionRevisions(revisions[index + 1]?.snapshot, revision.snapshot),
    })),
  };
}
